import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button, Dropdown, Menu, Badge, notification } from 'antd';
import { DownloadOutlined, ClockCircleOutlined, CheckCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import ExtendedExportModal from './ExtendedExportModal';
import CustomReportModal from './CustomReportModal';
import CustomReportConfigModal from './CustomReportConfigModal';
import { useSharedData } from '../contexts/SharedDataContext';

const ReportExportManager = ({
                                 currentConfig,
                                 dateRange,
                                 onShowCallExport,
                                 onShowScorecardExport,
                                 onSyncConfig,
                                 openExtendedExportModal
                             }) => {
    const [extendedModalVisible, setExtendedModalVisible] = useState(false);
    const [exportType, setExportType] = useState(null);
    const [pendingReports, setPendingReports] = useState([]);
    const [completedReports, setCompletedReports] = useState([]);
    const [customReports, setCustomReports] = useState([]);
    const [customReportConfigVisible, setCustomReportConfigVisible] = useState(false);
    const [customReportModalVisible, setCustomReportModalVisible] = useState(false);
    const [selectedCustomReport, setSelectedCustomReport] = useState(null);
    const [reportParams, setReportParams] = useState({});
    const [loading, setLoading] = useState(false);

    const { getAccessTokenSilently } = useAuth0();
    const { socket, useSocketEvent, selectedTenant } = useSharedData();

    // Fetch available custom reports on component mount and when tenant changes
    useEffect(() => {
        const fetchCustomReports = async () => {
            try {
                setLoading(true);
                const token = await getAccessTokenSilently();
                const response = await axios.get('/api/reports/custom', {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': selectedTenant,
                    }
                });

                setCustomReports(response.data.reports || []);
            } catch (error) {
                console.error('Error fetching custom reports:', error);
                // Don't show notification for this as it might be a common error
                // when reports aren't configured
                setCustomReports([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCustomReports();
    }, [getAccessTokenSilently, selectedTenant]);

    useEffect(() => {
        if (openExtendedExportModal) {
            openExtendedExportModal.current = (type) => {
                setExportType(type);
                setExtendedModalVisible(true);
            };
        }
    }, [openExtendedExportModal]);

    // Listen for report notifications via websocket using our custom hook
    useSocketEvent('reportComplete', (reportData) => {
        // Remove from pending list
        setPendingReports(prev => prev.filter(report => report.id !== reportData.id));

        // Add to completed list
        setCompletedReports(prev => [reportData, ...prev]);

        // Show notification
        notification.success({
            message: 'Report Ready',
            description: (
                <div>
                    Your report is ready to download.
                    <Button
                        type="link"
                        onClick={() => downloadReport(reportData)}
                        style={{ padding: '0', height: 'auto', marginLeft: '8px' }}
                    >
                        Download Now
                    </Button>
                </div>
            ),
            duration: 10,
            icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
        });
    });

    // Download report function
    const downloadReport = useCallback(async (report) => {
        try {
            const token = await getAccessTokenSilently();

            // Make an authenticated request to get the report file
            const response = await axios.get(`/api/reports/${report.id}/download`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant,
                },
                responseType: 'blob'
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${report.name || 'report'}.${report.format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            // Remove from completed list after download
            setCompletedReports(prev => prev.filter(r => r.id !== report.id));
        } catch (error) {
            console.error('Error downloading report:', error);
            notification.error({
                message: 'Download Failed',
                description: 'There was an error downloading your report. Please try again.'
            });
        }
    }, [getAccessTokenSilently, selectedTenant]);

    // Request extended export function
    const requestExtendedExport = useCallback(async (exportRequest) => {
        try {
            const token = await getAccessTokenSilently();
            const response = await axios.post('/api/extended-reports', exportRequest, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-tenant': selectedTenant,
                }
            });

            // Add to pending reports
            setPendingReports(prev => [...prev, response.data]);

            return response.data;
        } catch (error) {
            console.error('Error requesting extended export:', error);
            throw error;
        }
    }, [getAccessTokenSilently, selectedTenant]);

    // Handle selecting a custom report from the menu
    const handleSelectCustomReport = useCallback((report) => {
        setSelectedCustomReport(report);

        // If there are parameters, show the config modal
        if (report.parameters && Object.keys(report.parameters).length > 0) {
            setCustomReportConfigVisible(true);
        } else {
            // Otherwise, run report directly with no parameters
            handleRunCustomReport(report.name, {});
        }
    }, []);

    // Handle running a custom report
    const handleRunCustomReport = useCallback((reportName, params) => {
        setReportParams(params);
        setCustomReportModalVisible(true);
    }, []);

    // Handle menu item clicks
    const handleMenuClick = useCallback(({ key }) => {
        // Handle custom report selection
        if (key.startsWith('customReport-')) {
            const reportName = key.replace('customReport-', '');
            const report = customReports.find(r => r.name === reportName);
            if (report) {
                handleSelectCustomReport(report);
            }
            return;
        }

        switch (key) {
            case 'exportCalls':
                onShowCallExport();
                break;
            case 'exportScorecards':
                onShowScorecardExport();
                break;
            case 'extendCalls':
                setExportType('calls');
                setExtendedModalVisible(true);
                break;
            case 'extendScorecards':
                setExportType('scorecard');
                setExtendedModalVisible(true);
                break;
            case 'extendBoth':
                setExportType('both');
                setExtendedModalVisible(true);
                break;
            default:
                // Check if the key starts with 'download-' for completed reports
                if (key.startsWith('download-')) {
                    const reportId = key.replace('download-', '');
                    const report = completedReports.find(r => r.id === reportId);
                    if (report) {
                        downloadReport(report);
                    }
                }
        }
    }, [
        onShowCallExport,
        onShowScorecardExport,
        completedReports,
        downloadReport,
        customReports,
        handleSelectCustomReport
    ]);

    // Close extended modal handler
    const handleCloseExtendedModal = useCallback(() => {
        setExtendedModalVisible(false);
    }, []);

    // Close custom report config modal handler
    const handleCloseCustomReportConfig = useCallback(() => {
        setCustomReportConfigVisible(false);
        setSelectedCustomReport(null);
    }, []);

    // Close custom report modal handler
    const handleCloseCustomReportModal = useCallback(() => {
        setCustomReportModalVisible(false);
    }, []);

    // Dropdown menu
    const menu = useMemo(() => (
        <Menu onClick={handleMenuClick}>
            <Menu.ItemGroup title="Quick Export">
                <Menu.Item key="exportCalls" icon={<DownloadOutlined />}>
                    Export Calls
                </Menu.Item>
                {/*  <Menu.Item key="exportScorecards" icon={<DownloadOutlined />}>
                    Export Scorecards
                </Menu.Item> */}
            </Menu.ItemGroup>

            <Menu.Divider />

            <Menu.ItemGroup title="Extended Date Range">
                <Menu.Item key="extendCalls" icon={<DownloadOutlined />}>
                    Export Calls (Extended Dates)
                </Menu.Item>
                <Menu.Item key="extendScorecards" icon={<DownloadOutlined />}>
                    Export Scorecards (Extended Dates)
                </Menu.Item>
                <Menu.Item key="extendBoth" icon={<DownloadOutlined />}>
                    Export Both (Extended Dates)
                </Menu.Item>
            </Menu.ItemGroup>

            {customReports.length > 0 && (
                <>
                    <Menu.Divider />
                    <Menu.ItemGroup title="Custom Reports">
                        {customReports.map(report => (
                            <Menu.Item
                                key={`customReport-${report.name}`}
                                icon={<AppstoreOutlined />}
                            >
                                {report.title}
                            </Menu.Item>
                        ))}
                    </Menu.ItemGroup>
                </>
            )}

            {pendingReports.length > 0 && (
                <>
                    <Menu.Divider />
                    <Menu.ItemGroup title="Pending Reports">
                        {pendingReports.map(report => (
                            <Menu.Item
                                key={`pending-${report.id}`}
                                icon={<ClockCircleOutlined spin />}
                                disabled
                            >
                                {report.name || 'Report'} (Processing...)
                            </Menu.Item>
                        ))}
                    </Menu.ItemGroup>
                </>
            )}

            {completedReports.length > 0 && (
                <>
                    <Menu.Divider />
                    <Menu.ItemGroup title="Ready to Download">
                        {completedReports.map(report => (
                            <Menu.Item
                                key={`download-${report.id}`}
                                icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                            >
                                {report.name || 'Report'} ({report.format.toUpperCase()})
                            </Menu.Item>
                        ))}
                    </Menu.ItemGroup>
                </>
            )}
        </Menu>
    ), [handleMenuClick, pendingReports, completedReports, customReports]);

    // Calculate badge count for the button
    const badgeCount = pendingReports.length + completedReports.length;

    return (
        <>
            <Badge count={badgeCount} dot={badgeCount > 0}>
                <Dropdown overlay={menu} placement="bottomRight" arrow>
                    <Button
                        icon={<DownloadOutlined />}
                        style={{ marginLeft: '8px' }}
                        className={pendingReports.length > 0 ? 'button-processing' : ''}
                    />
                </Dropdown>
            </Badge>

            <ExtendedExportModal
                visible={extendedModalVisible}
                onClose={handleCloseExtendedModal}
                currentConfig={currentConfig}
                currentDateRange={dateRange}
                exportType={exportType}
                socket={socket}
                onSyncConfig={onSyncConfig}
                onRequestExport={requestExtendedExport}
            />

            {selectedCustomReport && (
                <CustomReportConfigModal
                    visible={customReportConfigVisible}
                    reportInfo={selectedCustomReport}
                    onClose={handleCloseCustomReportConfig}
                    onRunReport={handleRunCustomReport}
                />
            )}

            <CustomReportModal
                visible={customReportModalVisible}
                reportName={selectedCustomReport?.name}
                params={reportParams}
                onClose={handleCloseCustomReportModal}
            />
        </>
    );
};

// Export as a memoized component to prevent unnecessary re-renders
export default React.memo(ReportExportManager);
